import { VisitNodePassage } from "./types";
import { ComparisonVisitorEnum, ComparisonVisitors, CustomVisitorsComparisonVisitorsCompatibility } from "./visitors/comparison/comparisonVisitors";
import { CustomVisitorEnum, CustomVisitors } from "./visitors/custom/customVisitors";
import { DatapointsVisitorEnum, DatapointsVisitors } from "./visitors/datapoints/datapointsVisitors";
import { MultiRequestVisitorEnum, MultiRequestVisitors } from "./visitors/multiRequest/multiRequestVisitors";
import { NodesVisitorEnum, NodesVisitors } from "./visitors/nodes/nodesVisitors";
const visitorsReducer = (visitors) => (args) => {
    visitors.forEach((v) => {
        v(args);
    });
};
export const validateArguments = (functionName, expectedSize, args) => {
    if ((args?.length ?? 0) !== expectedSize) {
        throw new Error(`${functionName} expects its arguments to be of size ${expectedSize}. Found ${args?.length ?? 0}`);
    }
    if (expectedSize > 0 && (args?.filter((a) => a)?.length ?? 0) !== expectedSize) {
        throw new Error(`${functionName} expects all its arguments to be non-empty strings`);
    }
    return true;
};
export const buildDatapointsVisitor = (config) => {
    const forwardDatapointsVisitors = [];
    const backwardDatapointsVisitors = [];
    config?.datapointsVisitorsReducer?.filter((dv) => dv.method).forEach((dv) => {
        if (!Object.values(DatapointsVisitorEnum)?.includes(dv.method)) {
            throw new Error(`Invalid datapoints visitor enum method ${dv.method}`);
        }
        const args = DatapointsVisitors[dv.method];
        if (!args) {
            throw new Error(`Invalid arguments definition for datapoints visitor method ${dv.method}`);
        }
        if (args.composeBackward) {
            backwardDatapointsVisitors.push(args.visitor(dv?.arguments || [], config.compositeObjectBuilder));
        }
        else {
            forwardDatapointsVisitors.push(args.visitor(dv?.arguments || [], config.compositeObjectBuilder));
        }
    });
    let datapointsVisitors = [];
    datapointsVisitors = datapointsVisitors.concat(forwardDatapointsVisitors);
    datapointsVisitors = datapointsVisitors.concat(backwardDatapointsVisitors.reverse());
    return visitorsReducer(datapointsVisitors);
};
const visit = (staticData, depth, visitedDimensions, visitor, immutableContext) => {
    if (staticData) {
        const { root, dimensions } = staticData;
        root.forEach((visitable) => {
            const argsFactory = (vnp) => ({
                context: { staticData, visitNodePassage: vnp, visitedDimensions, ...immutableContext },
                visitable
            }), { dimension } = visitable;
            visitor(argsFactory(VisitNodePassage.Always));
            let { data } = visitable;
            if (data) {
                if (dimensions.length - 1 - depth > -1) {
                    visitedDimensions[dimensions[dimensions.length - 1 - depth]] = dimension?.toString() || "";
                }
                visitor(argsFactory(VisitNodePassage.NodeFirst));
                const visited = visit({ customResult: staticData.customResult, dimensions, root: data }, depth + 1, visitedDimensions, visitor, immutableContext);
                data = visited.root;
                staticData.customResult = visited.customResult;
                visitor(argsFactory(VisitNodePassage.NodeLast));
            }
            else {
                visitor(argsFactory(VisitNodePassage.Leaf));
            }
        });
        if (depth === 0) {
            visitor({ context: { staticData, visitNodePassage: VisitNodePassage.RootLast, visitedDimensions, ...immutableContext }, visitable: {} });
        }
    }
    return staticData;
};
const visitRootNode = (args, visitors) => {
    const fixedContext = {
        componentProps: args.context.componentProps,
        datapointsMetadata: args.context.datapointsMetadata,
        request: args.context.request,
        widgetState: args.context.widgetState
    };
    visit(args.context.staticData, 0, {}, visitorsReducer(visitors), fixedContext);
};
export const visitCompositeObject = (root, dimensions, visitor) => {
    visitRootNode({
        context: { staticData: { dimensions, root }, visitNodePassage: VisitNodePassage.Always, visitedDimensions: {} },
        visitable: {}
    }, [visitor]);
};
export const buildNodesVisitor = (nodesVisitorsReducer) => {
    const nodesVisitors = [];
    nodesVisitorsReducer?.filter((nv) => nv.method).forEach((nv) => {
        if (!Object.values(NodesVisitorEnum)?.includes(nv.method)) {
            throw new Error(`Invalid nodes visitor enum method ${nv.method}`);
        }
        const args = NodesVisitors[nv.method];
        if (!args) {
            throw new Error(`Invalid arguments definition for nodes visitor method ${nv.method}`);
        }
        nodesVisitors.push(args.visitor(nv.arguments || []));
    });
    return (args) => visitRootNode(args, nodesVisitors);
};
export const buildCustomVisitor = (customVisitorsReducer) => {
    const nodesVisitors = [];
    customVisitorsReducer?.filter((nv) => nv.method).forEach((nv) => {
        if (!Object.values(CustomVisitorEnum)?.includes(nv.method)) {
            throw new Error(`Invalid nodes visitor enum method ${nv.method}`);
        }
        const args = CustomVisitors[nv.method];
        if (!args) {
            throw new Error(`Invalid arguments definition for nodes visitor method ${nv.method}`);
        }
        nodesVisitors.push(args.visitor(nv.arguments || []));
    });
    return (args) => visitRootNode(args, nodesVisitors);
};
export const buildMultiRequestVisitor = (config) => {
    const multiRequestVisitors = [];
    config?.multiRequestVisitorsReducer?.filter((mrv) => mrv.method).forEach((mrv) => {
        if (!Object.values(MultiRequestVisitorEnum)?.includes(mrv.method)) {
            throw new Error(`Invalid datapoints visitor enum method ${mrv.method}`);
        }
        const args = MultiRequestVisitors[mrv.method];
        if (!args) {
            throw new Error(`Invalid arguments definition for datapoints visitor method ${mrv.method}`);
        }
        multiRequestVisitors.push(args.visitor(mrv?.arguments || []));
    });
    return visitorsReducer(multiRequestVisitors);
};
const validateComparisonVisitor = (method, customVisitorsReducer) => {
    const compatibilities = CustomVisitorsComparisonVisitorsCompatibility.get(method);
    if (compatibilities?.length && customVisitorsReducer?.length) {
        return customVisitorsReducer.every((v) => compatibilities.includes(v.method));
    }
    return false;
};
export const buildComparisonVisitor = (comparisonVisitorsReducer, customVisitorsReducer) => {
    const comparisonVisitors = [];
    comparisonVisitorsReducer?.filter((nv) => nv.method).forEach((nv) => {
        if (!Object.values(ComparisonVisitorEnum)?.includes(nv.method)) {
            throw new Error(`Invalid nodes visitor enum method ${nv.method}`);
        }
        const args = ComparisonVisitors[nv.method];
        if (!args) {
            throw new Error(`Invalid arguments definition for nodes visitor method ${nv.method}`);
        }
        if (!validateComparisonVisitor(nv.method, customVisitorsReducer)) {
            throw new Error(`Comparison visitor method ${nv.method} is incompatible with the custom visitor that is used`);
        }
        comparisonVisitors.push(args.visitor(nv.arguments || []));
    });
    return visitorsReducer(comparisonVisitors);
};
